import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  ArrowLeft,
  Printer,
  Clock,
  CheckCircle2,
  XCircle,
  Pause,
  Gauge,
  Package,
  Wrench,
  Users,
  AlertTriangle,
  Factory,
  BarChart3,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusLabels = { completed: "Concluída", running: "Em Execução", paused: "Pausada", cancelled: "Cancelada", planned: "Planeada", pending: "Pendente" };
const statusColors = { completed: "bg-green-500", running: "bg-blue-500", paused: "bg-amber-500", cancelled: "bg-red-500", planned: "bg-slate-500", pending: "bg-amber-500" };

function StatCard({ icon: Icon, label, value, sub, color = "text-foreground" }) {
  return (
    <div className="bg-white rounded-xl border p-4 text-center">
      <Icon className={`h-5 w-5 mx-auto mb-2 ${color}`} />
      <p className="text-2xl font-bold font-mono ${color}">{value}</p>
      <p className="text-xs text-muted-foreground mt-1">{label}</p>
      {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

export default function OrderReport() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const printRef = useRef();

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const r = await axios.get(`${API}/orders/${orderId}/report`);
        setReport(r.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, [orderId]);

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]"><Gauge className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  if (!report) {
    return <div className="text-center py-12 text-muted-foreground">Relatório não encontrado</div>;
  }

  const { order, part, mold, machine, production, time, efficiency, raw_material, operators, stops, defects } = report;

  return (
    <div className="space-y-6 max-w-5xl mx-auto" data-testid="order-report" ref={printRef}>
      {/* Header */}
      <div className="flex items-center justify-between print:hidden">
        <Button variant="ghost" onClick={() => navigate(-1)} className="gap-2">
          <ArrowLeft className="h-4 w-4" /> Voltar
        </Button>
        <Button onClick={handlePrint} className="gap-2 bg-amber-500 hover:bg-amber-600">
          <Printer className="h-4 w-4" /> Imprimir Relatório
        </Button>
      </div>

      {/* Print Header with Logo (only visible when printing) */}
      <div className="hidden print:block mb-4">
        <div className="flex justify-between items-center border-b-2 border-amber-500 pb-3">
          <img src="/logo-ep3d.png" alt="EP3D" className="h-12" />
          <div className="text-right text-xs text-slate-500">
            <p className="font-bold text-sm text-slate-800">Relatório de Ordem de Produção</p>
            <p>{order.code} | {new Date().toLocaleDateString("pt-PT")}</p>
          </div>
        </div>
      </div>

      {/* Title */}
      <div className="bg-slate-900 text-white rounded-xl p-6 print:rounded-none print:p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-amber-400 text-sm font-semibold uppercase tracking-wider">Relatório de Ordem de Produção</p>
            <h1 className="text-3xl font-bold mt-1 font-mono">{order.code}</h1>
          </div>
          <div className="text-right">
            <Badge className={`${statusColors[order.status]} text-white text-sm px-3 py-1`}>
              {statusLabels[order.status] || order.status}
            </Badge>
            {order.priority === "high" && <Badge className="bg-red-500 text-white ml-2">Alta Prioridade</Badge>}
            {order.priority === "urgent" && <Badge className="bg-red-600 text-white ml-2">Urgente</Badge>}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4 mt-4 text-sm">
          <div><span className="text-slate-400">Peça:</span> <span className="font-medium">{part?.code} - {part?.description}</span></div>
          <div><span className="text-slate-400">Molde:</span> <span className="font-medium">{mold?.code} ({mold?.cavities} cav. / {mold?.cycle_time}s)</span></div>
          <div><span className="text-slate-400">Máquina:</span> <span className="font-medium">{machine?.code} - {machine?.description}</span></div>
        </div>
      </div>

      {/* KPIs Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <StatCard icon={CheckCircle2} label="Peças Boas" value={production.good_quantity.toLocaleString()} color="text-green-600" />
        <StatCard icon={XCircle} label="Rejeitadas" value={production.rejected_quantity.toLocaleString()} color="text-red-600" />
        <StatCard icon={BarChart3} label="Taxa Qualidade" value={`${production.quality_rate}%`} color={production.quality_rate >= 95 ? "text-green-600" : "text-red-600"} />
        <StatCard icon={Gauge} label="Conclusão" value={`${production.completion_rate}%`} sub={`Obj: ${production.target.toLocaleString()}`} color="text-blue-600" />
        <StatCard icon={Clock} label="Tempo Total" value={time.total_time_formatted} color="text-slate-600" />
        <StatCard icon={Pause} label="Tempo Parado" value={time.stop_time_formatted} sub={`${stops.total_count} paragens`} color="text-amber-600" />
      </div>

      {/* Time & Efficiency */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Clock className="h-4 w-4" /> Tempos de Produção</CardTitle></CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <tbody>
                <tr className="border-b"><td className="py-2 text-muted-foreground">Início</td><td className="py-2 font-medium text-right">{order.started_at ? new Date(order.started_at).toLocaleString("pt-PT") : "-"}</td></tr>
                <tr className="border-b"><td className="py-2 text-muted-foreground">Fim</td><td className="py-2 font-medium text-right">{order.completed_at ? new Date(order.completed_at).toLocaleString("pt-PT") : "-"}</td></tr>
                <tr className="border-b"><td className="py-2 text-muted-foreground">Tempo Total</td><td className="py-2 font-bold text-right">{time.total_time_formatted}</td></tr>
                <tr className="border-b"><td className="py-2 text-muted-foreground">Tempo Paragens</td><td className="py-2 font-bold text-amber-600 text-right">{time.stop_time_formatted}</td></tr>
                <tr><td className="py-2 text-muted-foreground">Tempo Efetivo</td><td className="py-2 font-bold text-green-600 text-right">{time.effective_time_formatted}</td></tr>
              </tbody>
            </table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Gauge className="h-4 w-4" /> Eficiência</CardTitle></CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <tbody>
                <tr className="border-b"><td className="py-2 text-muted-foreground">Ritmo Teórico</td><td className="py-2 font-medium text-right">{efficiency.theoretical_rate} pç/h</td></tr>
                <tr className="border-b"><td className="py-2 text-muted-foreground">Ritmo Real</td><td className="py-2 font-medium text-right">{efficiency.actual_rate} pç/h</td></tr>
                <tr className="border-b">
                  <td className="py-2 text-muted-foreground">Eficiência</td>
                  <td className="py-2 font-bold text-right">
                    <span className={efficiency.efficiency_percent >= 85 ? "text-green-600" : efficiency.efficiency_percent >= 70 ? "text-amber-600" : "text-red-600"}>
                      {efficiency.efficiency_percent}%
                    </span>
                  </td>
                </tr>
                <tr className="border-b"><td className="py-2 text-muted-foreground">Matéria-Prima</td><td className="py-2 font-medium text-right">{raw_material.total_kg} kg</td></tr>
                <tr><td className="py-2 text-muted-foreground">Peso/Peça</td><td className="py-2 font-medium text-right">{raw_material.weight_per_piece_g} g</td></tr>
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>

      {/* Stops */}
      {stops.total_count > 0 && (
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Pause className="h-4 w-4 text-amber-500" /> Paragens ({stops.total_count})</CardTitle></CardHeader>
          <CardContent>
            {/* By reason */}
            {Object.keys(stops.by_reason).length > 0 && (
              <div className="mb-4">
                <p className="text-sm font-medium text-muted-foreground mb-2">Resumo por Motivo:</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {Object.entries(stops.by_reason).sort((a,b) => b[1] - a[1]).map(([reason, mins]) => (
                    <div key={reason} className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
                      <p className="font-bold text-amber-700">{Math.round(mins)} min</p>
                      <p className="text-xs text-amber-600 mt-1">{reason}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* Detail table */}
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="text-left px-3 py-2">Início</th>
                    <th className="text-left px-3 py-2">Fim</th>
                    <th className="text-left px-3 py-2">Motivo</th>
                    <th className="text-right px-3 py-2">Duração</th>
                    <th className="text-left px-3 py-2">Observações</th>
                  </tr>
                </thead>
                <tbody>
                  {stops.records.map((s, i) => (
                    <tr key={i} className="border-b">
                      <td className="px-3 py-1.5 font-mono">{s.started_at ? new Date(s.started_at).toLocaleString("pt-PT", {hour:"2-digit",minute:"2-digit",day:"2-digit",month:"2-digit"}) : "-"}</td>
                      <td className="px-3 py-1.5 font-mono">{s.ended_at ? new Date(s.ended_at).toLocaleString("pt-PT", {hour:"2-digit",minute:"2-digit",day:"2-digit",month:"2-digit"}) : <Badge className="bg-amber-500 text-white text-xs">Em curso</Badge>}</td>
                      <td className="px-3 py-1.5">{s.reason || "-"}</td>
                      <td className="px-3 py-1.5 text-right font-mono font-bold">{s.duration_minutes ? `${Math.round(s.duration_minutes)} min` : "-"}</td>
                      <td className="px-3 py-1.5 text-muted-foreground">{s.notes || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Defects */}
      {Object.keys(defects).length > 0 && (
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2 text-base"><AlertTriangle className="h-4 w-4 text-red-500" /> Defeitos por Tipo</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {Object.entries(defects).sort((a,b) => b[1] - a[1]).map(([type, count]) => (
                <div key={type} className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                  <p className="font-bold text-red-700">{count}</p>
                  <p className="text-xs text-red-600 mt-1">{type}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Operators */}
      {operators.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Users className="h-4 w-4" /> Operadores</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {operators.map((op, i) => (
                <Badge key={i} variant="outline" className="text-sm px-3 py-1">{op}</Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Notes */}
      {order.notes && (
        <Card>
          <CardHeader><CardTitle className="text-base">Notas</CardTitle></CardHeader>
          <CardContent><p className="text-sm text-muted-foreground">{order.notes}</p></CardContent>
        </Card>
      )}

      {/* Footer */}
      <div className="text-center text-xs text-muted-foreground py-4 border-t">
        <p>EP3D Produção — Relatório gerado em {new Date().toLocaleString("pt-PT")}</p>
        <p>Registos de produção: {report.production_records_count} | Engineering Project 3D</p>
      </div>
    </div>
  );
}
